import { gpHome } from '../../app';
import { dayjs } from '#gp/app-common/util/dayjs';
import { formatEasyToReadDate } from '../../../app-common/util/dateFormatting';
import {
    AiCreditsServiceName,
    AiCreditsRefreshEvent,
    AiCreditBalance,
    AiCreditProductUsage,
    AiCreditMemberUsage,
    IAiCreditsApi,
} from '../../services/ai-credits-contract';
import { shouldRefreshOnFocus } from './ai-credits-refresh-gate';
import { accountPercent } from './account-percent';
import { shouldShowAllotmentReset } from './allotment-reset-line';
import { withBarPercents, WithBarPercent } from './usage-bar-scale';
import { toDonutSegments, DonutView } from './usage-donut';
import { BuyCreditsDialogService } from '../gp-buy-credits-dialog/gp-buy-credits-dialog';
import { AiCreditsPurchaseServiceName, IAiCreditsPurchaseApi } from '../../services/ai-credits-purchase-contract';
import { AccountRefreshAllEvent, AccountPanelBusyEvent, AccountPanelBusyPayload } from '../../services/account-refresh-contract';
import {
    AiCreditPoolRow,
    AiCreditPoolTotals,
    buildPoolRows,
    shouldShowPoolList,
    totalAcrossPools,
} from './ai-credits-pool-rows';

import type * as ng from 'angular';
import aiCreditsTemplate from './ai-credits.html';

interface gpAiCreditsScope extends ng.IScope {
    balance: AiCreditBalance | null;
    resetDate: string;
    /** Whether the reset sentence may be drawn at all — see shouldShowAllotmentReset. */
    showResetDate: boolean;
    allotmentPercent: number;
    purchasedPercent: number;
    /**
     * The staff bar's own figures: its account renews on the calendar month, so it
     * cannot share resetDate/periodLabel with the subscription bar — a staff
     * subscriber shows both bars with different reset dates (#716).
     */
    staffPercent: number;
    staffResetDate: string;
    periodLabel: string;
    /**
     * Whether the pool being BILLED is a team's — i.e. whether the figures above are
     * shared money. Not "this user is on a team": since #724 a user can be on several
     * teams and still have their usage charged to their own pool, so this can only ever
     * describe the one pool the panel's headline figures belong to.
     */
    billedToTeam: boolean;
    /** Every pool this user can spend from (#724), in the order the server sent them. */
    poolRows: AiCreditPoolRow[];
    /**
     * The headline figures, summed across every pool — what this person has in total,
     * rather than what sits in the one pool being charged.
     */
    totals: AiCreditPoolTotals;
    /**
     * Internal staff see every pool on their account but always spend from their own
     * (#248), so the list cannot be headed "pools you can spend from" for them.
     */
    isStaffAccount: boolean;
    /** Whether the pool list is worth drawing at all — see shouldShowPoolList. */
    showPoolList: boolean;
    /**
     * Where the credits went, as a ring — features whose credits really came off the
     * balance, never complimentary usage. Part-to-whole, unlike the per-member list
     * below, which stays a set of comparable bars.
     */
    usageDonut: DonutView;
    usageByMember: WithBarPercent<AiCreditMemberUsage>[];
    /**
     * Credits consumed by non-billable (complimentary) usage this period. Kept out of
     * the donut because that ring is headed "Usage by feature" and totals credits that
     * were actually deducted; folding these in would make the centre figure disagree
     * with the balance above it.
     */
    includedCredits: number;

    loadingData: boolean;
    loadedData: boolean;
    errorText: string | null;

    refresh(): void;

    /**
     * Whether this deployment has a pack catalogue to sell. Nothing to buy means no
     * button, rather than a button that takes the user through picking a pack before
     * admitting purchasing isn't live.
     */
    showBuyCredits: boolean;
    buyCredits(): void;
}

gpHome.directive('gpAiCredits', [
    AiCreditsServiceName,
    AiCreditsPurchaseServiceName,
    BuyCreditsDialogService.serviceName,
    function (
        gpAiCreditsService: IAiCreditsApi,
        gpAiCreditsPurchaseService: IAiCreditsPurchaseApi,
        gpBuyCreditsDialogService: BuyCreditsDialogService,
    ) {
        function link($scope: gpAiCreditsScope) {
            $scope.balance = null;
            $scope.resetDate = '';
            $scope.showResetDate = false;
            $scope.allotmentPercent = 0;
            $scope.purchasedPercent = 0;
            $scope.staffPercent = 0;
            $scope.staffResetDate = '';
            $scope.periodLabel = 'this month';
            $scope.billedToTeam = false;
            $scope.poolRows = [];
            $scope.showPoolList = false;
            $scope.totals = totalAcrossPools(null);
            $scope.isStaffAccount = false;
            $scope.usageDonut = { segments: [], total: 0 };
            $scope.usageByMember = [];
            $scope.includedCredits = 0;
            $scope.loadingData = false;
            $scope.loadedData = false;
            $scope.errorText = null;
            $scope.showBuyCredits = false;

            let lastLoadStartedAtMs: number | null = null;

            /** Lets the page header's "Refresh all" spinner track this panel's fetches. */
            function reportBusy(busy: boolean) {
                $scope.$emit(AccountPanelBusyEvent, { panel: 'credits', busy } satisfies AccountPanelBusyPayload);
            }

            function loadCredits() {
                lastLoadStartedAtMs = Date.now();
                $scope.loadingData = true;
                $scope.errorText = null;
                reportBusy(true);

                gpAiCreditsService.getBalance()
                    .then(balance => {
                        $scope.balance = balance;
                        $scope.billedToTeam = balance.scope === 'team';

                        // Every pool this user can spend from (#724). The figures above
                        // describe only the billed one, so without this list a user on a
                        // team had no way to see the pool they weren't being charged to.
                        $scope.poolRows = buildPoolRows(balance);
                        $scope.showPoolList = shouldShowPoolList($scope.poolRows);

                        $scope.isStaffAccount = balance.tier === 'staff';

                        $scope.resetDate = balance.periodResetsUtc
                            ? formatEasyToReadDate(dayjs(balance.periodResetsUtc).local())
                            : '';
                        // Having a date is not enough to promise a reset (#740): the
                        // period the date closes must have granted an allotment.
                        $scope.showResetDate = shouldShowAllotmentReset(
                            $scope.resetDate, balance.monthlyAllotment);

                        // Summed over every pool, not just the billed one: the question
                        // the headline answers is "how many credits do I have?", and
                        // answering it from one pool made the page disagree with the list
                        // right underneath it (#724).
                        const totals = totalAcrossPools(balance);
                        $scope.totals = totals;

                        $scope.allotmentPercent = accountPercent(totals.allotmentUsed, totals.allotmentTotal);

                        // Purchased mirrors allotted: % used against the all-time total.
                        $scope.purchasedPercent = accountPercent(totals.purchasedUsed, totals.purchasedTotal);

                        // The staff bar stays on the billed pool's own figures rather than
                        // joining the #724 sum: the staff allotment is granted to the user's
                        // PERSONAL account only, and a staff user is always billed to that
                        // personal pool, so there is nothing in a team pool to add. Summing
                        // it would just be the same number through a wider code path.
                        //
                        // Its month window and reset date are likewise its own — deliberately
                        // not derived from periodResetsUtc, which follows the Stripe
                        // subscription period (#716).
                        $scope.staffPercent = accountPercent(balance.staffAllotmentUsed, balance.staffAllotmentMonthly);
                        $scope.staffResetDate = balance.staffResetsUtc
                            ? formatEasyToReadDate(dayjs(balance.staffResetsUtc).local())
                            : '';

                        // No billing interval (free tier) → drop the period suffix so
                        // the headline doesn't imply a monthly reset.
                        $scope.periodLabel = balance.billingInterval === 'year' ? 'this year'
                            : balance.billingInterval === 'month' ? 'this month' : '';

                        // Partition, don't merge. The "Usage by feature" table is headed
                        // "Credits used", so it may only contain credits that actually
                        // came off the balance; non-billable usage (the server's collapsed
                        // "Included" row) is reported separately as a total, where no
                        // column header claims it was deducted.
                        const products = balance.usageByProduct || [];
                        // toDonutSegments does its own ranking, folding and arc maths.
                        $scope.usageDonut = toDonutSegments(
                            products.filter(item => !item.nonBillable));
                        $scope.includedCredits = products
                            .filter(item => !!item.nonBillable)
                            .reduce((sum, item) => sum + (item.creditsUsed || 0), 0);
                        $scope.usageByMember = withBarPercents((balance.usageByMember || [])
                            .filter(member => member.creditsUsed > 0)
                            .sort((a, b) => b.creditsUsed - a.creditsUsed));

                        $scope.loadedData = true;
                    })
                    .catch(() => {
                        // A failed *refresh* is not a failed page: the figures already on
                        // screen are still the last ones the server gave us, so say the
                        // refresh didn't land and leave them up, rather than blanking the
                        // panel and collapsing the page behind it (#544). Only a failure
                        // with nothing to fall back on reports the panel as unavailable.
                        $scope.errorText = $scope.balance
                            ? 'Couldn\'t refresh just now — these are your last known figures.'
                            : 'AI credit usage is unavailable right now.';
                    })
                    .finally(() => {
                        $scope.loadingData = false;
                        reportBusy(false);
                    });
            }

            // Re-fetch on demand (the Refresh button). Usage is metered asynchronously,
            // so this reflects whatever the billing worker has posted so far.
            $scope.refresh = loadCredits;

            // Buying is offered from this panel because it is where the user finds out
            // they are running low. The dialog owns the catalogue and the checkout
            // redirect; gp-ai-credits-purchase handles the return from Stripe.
            $scope.buyCredits = () => {
                gpBuyCreditsDialogService.showDialog().catch(() => { /* dismissed */ });
            };

            // Offer the button only where there is something to sell. `available` is
            // the deployment's pack catalogue being populated — the same flag that used
            // to put a "Coming soon" chip beside the old Buy Credits heading. Fails
            // closed: if we can't establish that purchasing is live, don't offer it.
            gpAiCreditsPurchaseService.getOptions()
                .then(options => $scope.showBuyCredits = !!options.available)
                .catch(() => $scope.showBuyCredits = false);

            // The purchase panel broadcasts this after a confirmed top-up (#76) so
            // the freshly-bought credits appear without a manual refresh.
            $scope.$on(AiCreditsRefreshEvent, loadCredits);

            // "Refresh all" in the page header.
            $scope.$on(AccountRefreshAllEvent, () => {
                if (!$scope.loadingData) loadCredits();
            });

            // Usage lands seconds after the AI call, but an open account page never
            // re-asks — so a figure that was correct on load silently goes stale (#375).
            // Refresh when the tab regains attention. Deliberately NOT a timer: this
            // endpoint makes two live Stripe calls per request for paid users, so
            // polling would amplify that against every subscriber.
            function refreshOnFocus() {
                if (!shouldRefreshOnFocus($scope.loadingData, lastLoadStartedAtMs, Date.now())) return;
                // Claim the timestamp synchronously, before scheduling. visibilitychange
                // and focus both fire in the same tick when a tab is re-selected, and
                // $applyAsync doesn't dedup by function reference — it queues every call
                // and flushes on a later setTimeout(0) — so without claiming here first,
                // both events would pass the gate and queue two fetches.
                lastLoadStartedAtMs = Date.now();
                // The event fires outside Angular's digest, so schedule the load into one.
                $scope.$applyAsync(loadCredits);
            }

            function onVisibilityChange() {
                if (document.visibilityState === 'visible') refreshOnFocus();
            }

            window.addEventListener('focus', refreshOnFocus);
            document.addEventListener('visibilitychange', onVisibilityChange);
            $scope.$on('$destroy', () => {
                window.removeEventListener('focus', refreshOnFocus);
                document.removeEventListener('visibilitychange', onVisibilityChange);
            });

            loadCredits();
        }

        return ({
            restrict: 'E',
            scope: {},
            link: (scope: gpAiCreditsScope) => {
                link(scope);
            },
            template: aiCreditsTemplate,
        });
    }
]);
